// Общи типове/помощници за „Разнасяне на отчети": алокации по продукт и склад,
// формати за пари/количества/дати и ДДС факторът бруто/нето (себестойността е
// винаги нето; бруто = нето × (1+ставка) при регистрирана по ДДС фирма).
import type { Product, ReportPosting } from "../../../../api/inventory";

// Една консумация на продукт от конкретен склад (FIFO или конкретна партида).
export type PostingAllocation = {
  id: number;
  warehouseId: number;
  quantity: number | null;
  mode: "fifo" | "lot";
  lotId: number | null;
};

// productId → алокациите му (по един ред на избран склад).
export type PostingAllocationsByProduct = Record<number, PostingAllocation[]>;

let allocationSequence = 1;
export const newPostingAllocation = (
  warehouseId: number,
  quantity: number | null,
): PostingAllocation => ({
  id: allocationSequence++,
  warehouseId,
  quantity,
  mode: "fifo",
  lotId: null,
});

// Сума с десетична ТОЧКА (както останалите парични формати в модула).
export const formatMoney = (value: number) =>
  Number(value ?? 0)
    .toLocaleString("bg-BG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(",", ".");

// Брой продадени (рецепти/продукти × бройки) — без излишни нули (не е
// количество по мерна единица; те минават през formatQty(..., decimals)).
export const formatCount = (value: number) =>
  Number(value ?? 0).toLocaleString("bg-BG", { maximumFractionDigits: 3 });

export const formatDateBg = (iso: string | null) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("bg-BG", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// (productId) → фактор бруто/нето. Нерегистрирана фирма или продукт без
// ставка → 1 (бруто = нето).
export const buildGrossFactorOf = (
  products: Product[],
  vatRegistered: boolean,
) => {
  const vatRateByProduct = new Map(products.map((p) => [p.id, p.vatRate ?? 0]));
  return (productId: number) => {
    const rate = vatRateByProduct.get(productId) ?? 0;
    return vatRegistered && rate ? 1 + rate / 100 : 1;
  };
};

// Толеранс при сравняване на количества (закръгляния при разгъване на рецепти).
export const QTY_EPSILON = 0.0005;

// Решетката на „Изписване от склад" — една за заглавния ред, редовете и реда
// „Общо", за да се застъпват колоните точно: Продукт · Склад · За изписване ·
// Наличност · След · Нето · Бруто. Сборът на минимумите е ~744px с отстъпите
// (`WRITEOFF_GRID_MIN_CLASS`) — побира се в детайла на 1366px (меню 248 +
// списък 264) без хоризонтален скрол; мярката е само в „За изписване", затова
// останалите количествени колони са тесни, а тя — колкото заглавието си.
export const WRITEOFF_GRID_CLASS =
  "grid grid-cols-[minmax(160px,3fr)_minmax(96px,1fr)_minmax(100px,1fr)_minmax(84px,0.8fr)_minmax(72px,0.7fr)_minmax(72px,0.7fr)_minmax(72px,0.7fr)] items-center gap-x-2.5 px-3.5";
export const WRITEOFF_GRID_MIN_CLASS = "min-w-[744px]";

// Анулиран документ (от BE с редизайна; стар BE не връща анулирани изобщо)
export const isPostingAnnulled = (posting: ReportPosting) =>
  posting.status === "annulled" || !!posting.deletedAt;
